// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';

import PageContainer from '../components/layout/PageContainer';
import {
  FHAntdDateTimePicker,
  FHAutocomplete,
  FHButton,
  FHSelect,
  FProgressBar,
} from '../components/base';
import FHAntdDateRangePicker from '../components/base/FHAntdDateRangePicker';
import SingleClientView from '../components/layout/supervision-metrics/SingleClientView';
import AllClientsView from '../components/layout/supervision-metrics/AllClientsView';
import { clientActions, metricsActions, providerAssignmentActions } from '../store';
import { areUserRolesIn, generateWeekRanges } from '../helpers';
import {
  API_DATE_FORMAT,
  BackgroundTypes,
  ClinicalRelationships,
  DATE_DISPLAY_FORMAT,
  Roles,
} from '../constants';
import { ALL_CLIENTS_ID, SupervisionMetricsPeriod } from './constants';
import { ClinicalStatus } from '../admin/constants/general';

import type { RootState } from '../store';
import type { Moment } from 'moment';
import type { IClient, IOption, IProviderAssignment, SelectValueType } from '../utils/types';

export const disableMonths = (
  current: Date,
  period: 'month' | 'week',
  pastMonthToEnable?: number,
) => {
  // Disable months after the current month
  return (
    current &&
    ((pastMonthToEnable &&
      moment(current).isBefore(moment().startOf(period).subtract(pastMonthToEnable, period))) ||
      moment(current).isAfter(moment().endOf(period)))
  );
};

function SupervisionMetrics() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { list: allClientsFetchedForOverridingRoleUsers = [] } = useSelector(
    (state: RootState) => state.clients,
  );
  const { loading: metricsLoading } = useSelector((state: RootState) => state.metrics);

  const [selectedClient, setSelectedClient] = useState<IOption | null>();
  const [period, setPeriod] = useState<'month' | 'week'>(SupervisionMetricsPeriod.Month);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [dateList, setDateList] = useState<{ startDate: string; endDate: string }[]>([]);
  const [showTables, setShowTables] = useState<boolean>(false);

  const { provider, loading: providerLoading } = useSelector((state: any) => state.providers);

  const clientOptionsForBCBAs = useMemo(() => {
    return [
      { id: ALL_CLIENTS_ID, label: 'All Clients' },
      ...providerAssignments
        .filter(
          (assignment: IProviderAssignment) =>
            assignment.client?.clinical_status === ClinicalStatus.CLINICAL,
        )
        .map((assignment: IProviderAssignment) => ({
          id: assignment.client?.id,
          label: `${assignment.client?.first_name} ${assignment.client?.last_name}`,
        })),
    ];
  }, [providerAssignments]);

  const clientOptionsForOverridingRoleUsers = useMemo(() => {
    return [
      ...allClientsFetchedForOverridingRoleUsers.map((client: IClient) => ({
        id: client?.id,
        label: `${client?.first_name} ${client?.last_name}`,
      })),
    ];
  }, [allClientsFetchedForOverridingRoleUsers]);

  useEffect(() => {
    if (areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager])) {
      dispatch(clientActions.getList({ getAccessTokenSilently }));
    }
  }, [user]);

  useEffect(() => {
    if (!provider) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [ClinicalRelationships.HomeBcba],
      }),
    );

    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  const handleSelectDate = (date: Moment) => {
    const isMonthly = period === SupervisionMetricsPeriod.Month ? true : false;
    setStartDate(moment(date).startOf(isMonthly ? 'month' : 'week'));
    setEndDate(moment(date).endOf(isMonthly ? 'month' : 'week'));
    setShowTables(false);
    dispatch(metricsActions.clearState());
  };

  const handleDateRangeChange = (value: [Moment, Moment] | null | [null, null]) => {
    if (!value) {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    const [startDateVal, endDateVal] = value;
    const isMonthly = period === SupervisionMetricsPeriod.Month ? true : false;

    if (startDateVal) {
      setStartDate(moment(startDateVal).startOf(isMonthly ? 'month' : 'week'));
    }
    if (endDateVal) {
      setEndDate(moment(endDateVal).endOf(isMonthly ? 'month' : 'week'));
    }
    setDateList([]);
    setShowTables(false);
    dispatch(metricsActions.clearState());
  };

  const handleGenerateReport = () => {
    if (!startDate || !endDate) return;
    setDateList([]);
    setShowTables(true);

    if (selectedClient?.id === ALL_CLIENTS_ID) {
      // Fetch data for all clients view
      dispatch(
        metricsActions.getSupervisionMetrics({
          getAccessTokenSilently,
          start: moment(startDate).format(API_DATE_FORMAT),
          end: moment(endDate).format(API_DATE_FORMAT),
          exclude_zero_session_clients: false,
        }),
      );
    } else {
      // Generate date list for single clients view
      const dateRangeList = generateWeekRanges(startDate, endDate, period);
      setDateList(dateRangeList);
    }
  };

  const handleClientSelect = (value: IOption | null) => {
    if (!value) return;
    setSelectedClient(value);
    const isAllClient = value.id === ALL_CLIENTS_ID;
    setDateList([]);
    setShowTables(false);

    if (
      (selectedClient?.id === ALL_CLIENTS_ID && !isAllClient) ||
      (selectedClient?.id !== ALL_CLIENTS_ID && isAllClient)
    ) {
      setPeriod(isAllClient ? SupervisionMetricsPeriod.Month : SupervisionMetricsPeriod.Week);
    }

    // Do not reset the date is user is switching between client in single client view
    if (selectedClient?.id !== ALL_CLIENTS_ID && !isAllClient) {
      return;
    }
    setStartDate(null);
    setEndDate(null);
    setShowTables(false);
    dispatch(metricsActions.clearState());
  };

  const handlePeriodChange = (value: SelectValueType) => {
    if (value !== SupervisionMetricsPeriod.Month && value !== SupervisionMetricsPeriod.Week) {
      return;
    }
    setPeriod(value);
    setStartDate(null);
    setEndDate(null);
    setShowTables(false);
    setDateList([]);
    setShowTables(false);
    dispatch(metricsActions.clearState());
  };

  const selectedClientId =
    selectedClient?.id === ALL_CLIENTS_ID ? null : Number(selectedClient?.id);

  const dateRender = (date: Date) => {
    const current = moment(date);
    if (
      startDate &&
      endDate &&
      current.isSameOrAfter(startDate, 'day') &&
      current.isSameOrBefore(endDate, 'day')
    ) {
      // Highlight the cell if within the range
      return (
        <div
          className="ant-picker-cell-inner"
          style={{
            fontWeight: 700,
          }}
        >
          {current.date()}
        </div>
      );
    }
    return <div className="ant-picker-cell-inner">{current.date()}</div>;
  };

  return (
    <PageContainer>
      {(providerAssignmentLoading || providerLoading) && <FProgressBar />}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box sx={{ display: 'flex' }}>
          <Box>
            <Typography fontWeight={700} textAlign="left" mb={1}>
              Client
            </Typography>
            <FHAutocomplete
              sx={{ width: 200, boxShadow: 'none' }}
              coloredBackground
              disableClearable
              placeholder="Select client"
              disabled={providerAssignmentLoading}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              options={
                areUserRolesIn(user?.roles, [Roles.Admin, Roles.ClientSuccessManager])
                  ? clientOptionsForOverridingRoleUsers
                  : clientOptionsForBCBAs
              }
              value={selectedClient}
              onChange={(_: any, newValue: IOption | null) => {
                handleClientSelect(newValue);
              }}
            />
          </Box>
          <Box ml={3}>
            <Typography fontWeight={700} textAlign="left" mb={1}>
              View by
            </Typography>
            <FHSelect
              disabled={metricsLoading}
              sx={{ width: 200, boxShadow: 'none' }}
              displayEmpty
              backgroundType={BackgroundTypes.Colored}
              options={[
                { value: SupervisionMetricsPeriod.Month, label: 'Monthly' },
                { value: SupervisionMetricsPeriod.Week, label: 'Weekly' },
              ]}
              value={period}
              onUpdate={(value: SelectValueType) => handlePeriodChange(value)}
            />
          </Box>
          <Box ml={3}>
            <Typography fontWeight={700} textAlign="left" mb={1}>
              {period === SupervisionMetricsPeriod.Month && selectedClient?.id === ALL_CLIENTS_ID
                ? 'Month'
                : 'Time Period'}
            </Typography>
            {selectedClient?.id === ALL_CLIENTS_ID ? (
              <FHAntdDateTimePicker
                coloredBackground
                format={
                  period === SupervisionMetricsPeriod.Month
                    ? 'MMM YY'
                    : () => {
                        if (!startDate && !endDate) return 'Select week';
                        return `${moment(startDate).format(DATE_DISPLAY_FORMAT)} - ${moment(
                          endDate,
                        ).format(DATE_DISPLAY_FORMAT)}`;
                      }
                }
                picker={period}
                disabledDate={(current: Date) =>
                  period === SupervisionMetricsPeriod.Month
                    ? disableMonths(current, 'month')
                    : disableMonths(current, 'week')
                }
                changeOnBlur={true}
                allowClear={false}
                value={startDate}
                disabled={metricsLoading}
                onChange={handleSelectDate}
                onCalendarChange={handleSelectDate}
                sx={{ width: '240px', boxShadow: 'none' }}
              />
            ) : (
              <FHAntdDateRangePicker
                coloredBackground
                format={period === SupervisionMetricsPeriod.Month ? 'DD MMM YY' : 'MM/DD/YY'}
                picker={period}
                changeOnBlur={true}
                allowClear={false}
                value={[startDate, endDate]}
                disabled={metricsLoading}
                dateRender={dateRender}
                disabledDate={(current: Date) =>
                  period === SupervisionMetricsPeriod.Month
                    ? disableMonths(current, 'month', 5)
                    : disableMonths(current, 'week', 15)
                }
                onChange={handleDateRangeChange}
                onCalendarChange={handleDateRangeChange}
                sx={{ width: '240px', boxShadow: 'none' }}
              />
            )}
          </Box>
        </Box>
        <FHButton
          disabled={!startDate || !endDate || metricsLoading}
          loading={metricsLoading}
          onClick={handleGenerateReport}
          sx={{ width: '200px', mt: 4 }}
        >
          Generate report
        </FHButton>
      </Box>
      <Box mt={selectedClient?.id === ALL_CLIENTS_ID ? 2 : 4}>
        {selectedClient?.id === ALL_CLIENTS_ID && showTables && <AllClientsView />}
        {selectedClientId !== null &&
          Number.isFinite(selectedClientId) &&
          showTables &&
          dateList.length > 0 && (
            <SingleClientView id={selectedClientId} dateList={dateList} period={period} />
          )}
      </Box>
    </PageContainer>
  );
}

export default SupervisionMetrics;
